import React, { useState, useEffect } from "react";
import { reportAPI, clientAPI } from "../lib/api";
import { formatCurrency, formatDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Loader2,
  Download,
  FileSpreadsheet,
  Calendar,
  TrendingUp,
  Users,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  IndianRupee,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState("ageing");
  
  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-slate-500">View ageing reports, GST summaries, and employee reimbursements</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="ageing" data-testid="ageing-report-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            Ageing Report
          </TabsTrigger>
          <TabsTrigger value="gst" data-testid="gst-report-tab">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            GST Summary
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="ledger-report-tab">
            <Calendar className="w-4 h-4 mr-2" />
            Client Ledger
          </TabsTrigger>
          <TabsTrigger value="reimbursement" data-testid="reimbursement-report-tab">
            <Users className="w-4 h-4 mr-2" />
            Employee Reimbursements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ageing" className="mt-6">
          <AgeingReport />
        </TabsContent>

        <TabsContent value="gst" className="mt-6">
          <GSTReport />
        </TabsContent>

        <TabsContent value="ledger" className="mt-6">
          <LedgerReport />
        </TabsContent>

        <TabsContent value="reimbursement" className="mt-6">
          <EmployeeReimbursementReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AgeingReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await reportAPI.ageing();
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load ageing report");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Current</p>
            <p className="text-lg font-bold font-mono text-emerald-600">
              {formatCurrency(data?.totals?.current || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">1-30 Days</p>
            <p className="text-lg font-bold font-mono text-amber-600">
              {formatCurrency(data?.totals?.days_1_30 || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">31-60 Days</p>
            <p className="text-lg font-bold font-mono text-orange-600">
              {formatCurrency(data?.totals?.days_31_60 || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">61-90 Days</p>
            <p className="text-lg font-bold font-mono text-red-500">
              {formatCurrency(data?.totals?.days_61_90 || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">90+ Days</p>
            <p className="text-lg font-bold font-mono text-red-600">
              {formatCurrency(data?.totals?.days_90_plus || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-4">
            <p className="text-xs text-slate-300 mb-1">Total Outstanding</p>
            <p className="text-lg font-bold font-mono">
              {formatCurrency(data?.totals?.total || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Client-wise Ageing</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data?.data?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right">1-30 Days</TableHead>
                  <TableHead className="text-right">31-60 Days</TableHead>
                  <TableHead className="text-right">61-90 Days</TableHead>
                  <TableHead className="text-right">90+ Days</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((row) => (
                  <TableRow key={row.client_id}>
                    <TableCell className="font-medium">{row.client_name}</TableCell>
                    <TableCell className="text-right font-mono">
                      {row.current > 0 ? formatCurrency(row.current) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.days_1_30 > 0 ? formatCurrency(row.days_1_30) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.days_31_60 > 0 ? formatCurrency(row.days_31_60) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.days_61_90 > 0 ? formatCurrency(row.days_61_90) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.days_90_plus > 0 ? formatCurrency(row.days_90_plus) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {formatCurrency(row.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No outstanding invoices</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GSTReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await reportAPI.gstSummary(fromDate, toDate);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load GST report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <Button onClick={fetchData} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Generate Report"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Taxable Amount</p>
                <p className="text-lg font-bold font-mono">
                  {formatCurrency(data.totals?.taxable_amount || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">IGST</p>
                <p className="text-lg font-bold font-mono">
                  {formatCurrency(data.totals?.igst || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">CGST</p>
                <p className="text-lg font-bold font-mono">
                  {formatCurrency(data.totals?.cgst || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">SGST</p>
                <p className="text-lg font-bold font-mono">
                  {formatCurrency(data.totals?.sgst || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Tax</p>
                <p className="text-lg font-bold font-mono text-amber-600">
                  {formatCurrency(data.totals?.total_tax || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 text-white">
              <CardContent className="p-4">
                <p className="text-xs text-slate-300 mb-1">Total Value</p>
                <p className="text-lg font-bold font-mono">
                  {formatCurrency(data.totals?.total_value || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* GSTR-1 Data */}
          <Card>
            <CardHeader>
              <CardTitle>GSTR-1 Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {data.gstr1_data?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>GSTIN</TableHead>
                      <TableHead className="text-right">Taxable</TableHead>
                      <TableHead className="text-right">IGST</TableHead>
                      <TableHead className="text-right">CGST</TableHead>
                      <TableHead className="text-right">SGST</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.gstr1_data.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-sm">{row.invoice_number}</TableCell>
                        <TableCell>{formatDate(row.invoice_date)}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{row.client_name}</TableCell>
                        <TableCell className="font-mono text-xs">{row.client_gstin || "-"}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(row.taxable_amount)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(row.igst)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(row.cgst)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(row.sgst)}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatCurrency(row.grand_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>No invoices in selected period</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* HSN Summary */}
          {data.hsn_summary?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>HSN Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>HSN Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Taxable Value</TableHead>
                      <TableHead className="text-right">Total Tax</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.hsn_summary.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono">{row.hsn_code}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{row.description}</TableCell>
                        <TableCell className="text-right font-mono">{row.quantity}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(row.taxable_value)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(row.total_tax)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function LedgerReport() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.list();
      setClients(response.data);
    } catch (error) {
      console.error("Failed to load clients");
    }
  };

  const fetchLedger = async () => {
    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }

    setLoading(true);
    try {
      const response = await reportAPI.clientLedger(selectedClient);
      setLedger(response.data);
    } catch (error) {
      toast.error("Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label>Select Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchLedger} disabled={loading || !selectedClient}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "View Ledger"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {ledger && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="md:col-span-2">
              <CardContent className="p-4">
                <h3 className="font-bold text-lg">{ledger.client?.name}</h3>
                {ledger.client?.gstin && (
                  <p className="text-sm text-slate-500 font-mono">GSTIN: {ledger.client.gstin}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Invoiced</p>
                <p className="text-lg font-bold font-mono">
                  {formatCurrency(ledger.total_invoiced || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className={ledger.outstanding > 0 ? "bg-amber-50" : "bg-emerald-50"}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Outstanding</p>
                <p className={`text-lg font-bold font-mono ${ledger.outstanding > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                  {formatCurrency(ledger.outstanding || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Ledger Table */}
          <Card>
            <CardHeader>
              <CardTitle>Ledger Entries</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {ledger.entries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.entries.map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDate(entry.date)}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.reference}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell className="text-right font-mono">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(entry.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>No transactions found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function EmployeeReimbursementReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [expandedEmployees, setExpandedEmployees] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        from_date: fromDate,
        to_date: toDate,
      };
      if (employeeFilter) {
        params.employee_name = employeeFilter;
      }
      const response = await reportAPI.employeeReimbursements(params);
      setData(response.data);
      // Auto-expand all employees
      const expanded = {};
      response.data.employees?.forEach((emp) => {
        expanded[emp.employee_name] = true;
      });
      setExpandedEmployees(expanded);
    } catch (error) {
      toast.error("Failed to load reimbursement report");
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (name) => {
    setExpandedEmployees((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const exportToExcel = () => {
    if (!data?.employees?.length) {
      toast.error("No data to export");
      return;
    }

    // Build CSV content
    let csv = "Employee Reimbursement Report\n";
    csv += `Period: ${fromDate} to ${toDate}\n\n`;
    csv += "Employee Name,Total Paid,Amount Reimbursed,Pending Reimbursement,Expense Count\n";
    
    data.employees.forEach((emp) => {
      csv += `"${emp.employee_name}",${emp.total_paid},${emp.amount_reimbursed},${emp.pending_reimbursement},${emp.expense_count}\n`;
    });

    csv += `\nTotal,${data.summary.total_employee_expenses},${data.summary.total_reimbursed},${data.summary.total_pending},${data.summary.pending_expense_count}\n`;

    csv += "\n\nExpense Details\n";
    csv += "Employee,Expense #,Description,Amount,Date,Category,Status,Reimbursement Date,Reference\n";

    data.employees.forEach((emp) => {
      emp.expenses.forEach((exp) => {
        csv += `"${emp.employee_name}","${exp.expense_number}","${exp.description}",${exp.amount},"${exp.expense_date?.split("T")[0] || ""}","${exp.category}","${exp.is_reimbursed ? "Reimbursed" : "Pending"}","${exp.reimbursement_date?.split("T")[0] || ""}","${exp.reimbursement_reference || ""}"\n`;
      });
    });

    // Download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Employee_Reimbursement_Report_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Report exported successfully");
  };

  const exportToPDF = () => {
    // Create a printable view
    const printContent = document.getElementById("reimbursement-report-content");
    if (!printContent) {
      toast.error("No content to print");
      return;
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Employee Reimbursement Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          h2 { font-size: 14px; margin: 15px 0 10px; color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          .period { color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .text-right { text-align: right; }
          .summary-card { display: inline-block; padding: 10px 20px; margin: 5px; border: 1px solid #ddd; border-radius: 5px; }
          .summary-label { font-size: 10px; color: #666; }
          .summary-value { font-size: 16px; font-weight: bold; }
          .pending { color: #dc2626; }
          .reimbursed { color: #16a34a; }
          .emp-header { background: #f8f8f8; font-weight: bold; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Employee Reimbursement Report</h1>
        <p class="period">Period: ${fromDate} to ${toDate}</p>
        
        <div style="margin-bottom: 20px;">
          <div class="summary-card">
            <div class="summary-label">Total Employee Expenses</div>
            <div class="summary-value">₹${(data?.summary?.total_employee_expenses || 0).toLocaleString("en-IN")}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Amount Reimbursed</div>
            <div class="summary-value reimbursed">₹${(data?.summary?.total_reimbursed || 0).toLocaleString("en-IN")}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Pending Reimbursement</div>
            <div class="summary-value pending">₹${(data?.summary?.total_pending || 0).toLocaleString("en-IN")}</div>
          </div>
        </div>

        ${data?.employees?.map((emp) => `
          <h2>${emp.employee_name}</h2>
          <p>Total: ₹${emp.total_paid.toLocaleString("en-IN")} | Reimbursed: ₹${emp.amount_reimbursed.toLocaleString("en-IN")} | <span class="pending">Pending: ₹${emp.pending_reimbursement.toLocaleString("en-IN")}</span></p>
          <table>
            <thead>
              <tr>
                <th>Expense #</th>
                <th>Description</th>
                <th>Date</th>
                <th>Category</th>
                <th class="text-right">Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${emp.expenses.map((exp) => `
                <tr>
                  <td>${exp.expense_number}</td>
                  <td>${exp.description}</td>
                  <td>${exp.expense_date?.split("T")[0] || "-"}</td>
                  <td>${exp.category}</td>
                  <td class="text-right">₹${exp.amount.toLocaleString("en-IN")}</td>
                  <td class="${exp.is_reimbursed ? "reimbursed" : "pending"}">${exp.is_reimbursed ? "Reimbursed" : "Pending"}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        `).join("") || "<p>No data available</p>"}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-2">
              <Label>Employee Name</Label>
              <Input
                type="text"
                placeholder="Filter by employee..."
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                className="w-[200px]"
              />
            </div>
            <Button onClick={fetchData} disabled={loading} data-testid="generate-reimbursement-report">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Generate Report"
              )}
            </Button>
            {data && (
              <>
                <Button variant="outline" onClick={exportToExcel} data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button variant="outline" onClick={exportToPDF} data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {data && (
        <div id="reimbursement-report-content">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Employees</p>
                <p className="text-2xl font-bold">
                  {data.summary?.total_employees || 0}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Employee Expenses</p>
                <p className="text-xl font-bold font-mono">
                  {formatCurrency(data.summary?.total_employee_expenses || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <p className="text-xs text-emerald-700 mb-1">Amount Reimbursed</p>
                <p className="text-xl font-bold font-mono text-emerald-700">
                  {formatCurrency(data.summary?.total_reimbursed || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <p className="text-xs text-red-700 mb-1">Pending Reimbursement</p>
                <p className="text-xl font-bold font-mono text-red-700">
                  {formatCurrency(data.summary?.total_pending || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Pending Expenses</p>
                <p className="text-2xl font-bold text-amber-600">
                  {data.summary?.pending_expense_count || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Employee-wise Details */}
          {data.employees?.length > 0 ? (
            <div className="space-y-4">
              {data.employees.map((employee) => (
                <Card key={employee.employee_name}>
                  <Collapsible
                    open={expandedEmployees[employee.employee_name]}
                    onOpenChange={() => toggleEmployee(employee.employee_name)}
                  >
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {expandedEmployees[employee.employee_name] ? (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            )}
                            <div className="p-2 bg-blue-100 rounded-full">
                              <Users className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{employee.employee_name}</CardTitle>
                              <p className="text-sm text-slate-500">
                                {employee.expense_count} expenses
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-right">
                            <div>
                              <p className="text-xs text-slate-500">Total Paid</p>
                              <p className="font-mono font-bold">{formatCurrency(employee.total_paid)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-emerald-600">Reimbursed</p>
                              <p className="font-mono font-bold text-emerald-600">
                                {formatCurrency(employee.amount_reimbursed)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-red-600">Pending</p>
                              <p className="font-mono font-bold text-red-600">
                                {formatCurrency(employee.pending_reimbursement)}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {employee.pending_count > 0 && (
                                <Badge variant="destructive">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {employee.pending_count} pending
                                </Badge>
                              )}
                              {employee.reimbursed_count > 0 && (
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {employee.reimbursed_count} reimbursed
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Expense #</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Reimbursement Date</TableHead>
                              <TableHead>Reference</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {employee.expenses.map((expense) => (
                              <TableRow key={expense.id}>
                                <TableCell>
                                  <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                                    {expense.expense_number}
                                  </code>
                                </TableCell>
                                <TableCell className="font-medium">{expense.description}</TableCell>
                                <TableCell>{formatDate(expense.expense_date)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="capitalize">
                                    {expense.category?.replace(/_/g, " ")}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-mono font-medium">
                                  {formatCurrency(expense.amount)}
                                </TableCell>
                                <TableCell>
                                  {expense.is_reimbursed ? (
                                    <Badge className="bg-emerald-100 text-emerald-700">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Reimbursed
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-700">
                                      <Clock className="w-3 h-3 mr-1" />
                                      Pending
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {expense.reimbursement_date ? formatDate(expense.reimbursement_date) : "-"}
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">
                                  {expense.reimbursement_reference || "-"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No employee expenses found in the selected period</p>
                <p className="text-sm mt-1">Try adjusting the date range or filters</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {!data && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <IndianRupee className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Click "Generate Report" to view employee reimbursement data</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
